import './styles.scss';

import { Icon, Loader } from '@ets-global/b2c-website-ui';
import classNames from 'classnames';
import { fromJS } from 'immutable';
import PropTypes from 'prop-types';
import React, { Fragment, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import ConfirmModal from '../../../../../components/Base/Modal/ConfirmModal';
import CandidatePictureUpload from '../../../../../components/CandidatePictureUpload';
import { useDateToString } from '../../../../../components/Element/DateToString';
import List from '../../../../../components/Element/List';
import { translateProductNameCallback } from '../../../../../helpers/i18n';
import AddOrUpdateCandidateForm from '../Form';

const CandidateWidget = ({
  candidate,
  order,
  showForm = false,
  onEdit,
  onDelete,
  onOpen,
  onClose,
  sessionId,
  testType,
  hasVoucher = false,
  notEditable = false,
  loading = false,
  withPicture = false,
  withDateOfBirth = false,
  displayOnly = false,
  productsAndServices,
}) => {
  const { i18n, t } = useTranslation();
  const translateProductName = translateProductNameCallback(i18n, t);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const DateToString = useDateToString();

  const options = [];

  productsAndServices.includedProductsAndServices.forEach((includedProduct) => {
    options.push(`1 ${translateProductName(includedProduct.translationKey)}`);
  });

  candidate.productsAndServices.forEach((candidatePS) => {
    productsAndServices.correctionProductsAndServices.forEach((ps) => {
      if (ps.id === candidatePS.id) {
        return options.push(`${candidatePS.quantity} ${translateProductName(ps.translationKey)}`);
      }
    });
    productsAndServices.discountProductsAndServices.forEach((ps) => {
      if (ps.id === candidatePS.id) {
        return options.push(`${candidatePS.quantity} ${translateProductName(ps.translationKey)}`);
      }
    });
    productsAndServices.certificateProductsAndServices.forEach((ps) => {
      if (ps.id === candidatePS.id) {
        options.push(`${candidatePS.quantity} ${translateProductName(ps.translationKey)}`);
      }
    });
  });

  const onRemoveConfirm = () => {
    const deleteCallback = onDelete(candidate);
    if (deleteCallback instanceof Promise) {
      return deleteCallback.then(() => {
        setDeleteModalOpen(false);
      });
    }
    setDeleteModalOpen(false);
  };

  return showForm ? (
    <AddOrUpdateCandidateForm
      candidate={candidate}
      order={order + 1}
      sessionId={sessionId}
      notEditable={notEditable}
      onComplete={(candidate) => onEdit(candidate)}
      onCancel={() => onClose()}
      productsAndServices={productsAndServices}
      withPicture={withPicture}
      withDateOfBirth={withDateOfBirth}
      loading={loading}
      testType={testType}
    />
  ) : (
    <div
      className={classNames('account-session-candidate account-session-candidate--options', {
        'account-session-candidate--without-picture': !withPicture,
      })}
    >
      <div className="account-session-candidate__flex">
        <div className="account-session-candidate__left account-session-candidate__left--full">
          {withPicture && (
            <CandidatePictureUpload
              sessionId={sessionId}
              candidate={candidate}
              withInstructions={false}
              displayOnly={displayOnly}
              onFinished={(candidate) => onEdit(candidate)}
            />
          )}
          <p className="account-session-candidate__name">{`${candidate.firstname} ${candidate.lastname}`}</p>
          <p className="account-session-candidate__email">{`${candidate.email}`}</p>
          {withDateOfBirth && candidate.dateOfBirth && (
            <p className="account-session-candidate__dateofbirth">
              <DateToString date={candidate.dateOfBirth} format="DD/MM/YYYY" />
            </p>
          )}
          <List items={fromJS(options)} listClassNames={'account-session-candidate__options'} />
        </div>
        <div className="account-session-candidate__right">
          {!loading && !notEditable && (
            <Fragment>
              <button onClick={() => (hasVoucher ? setEditModalOpen(true) : onOpen())} aria-label="open-edit-modal">
                <Icon name={'edit'} aria-hidden={'true'} />
              </button>
              {hasVoucher && (
                <ConfirmModal
                  isOpen={isEditModalOpen}
                  title={t('session-registration.edit-candidate-with-voucher-modal.title')}
                  onClose={() => setEditModalOpen(false)}
                  buttons={[
                    {
                      label: t('common.cancel'),
                      className: 'button--transparent-light',
                      onClick: () => setEditModalOpen(false),
                    },
                    {
                      label: t('common.validate'),
                      loading,
                      onClick: () => {
                        setEditModalOpen(false);
                        onOpen();
                      },
                    },
                  ]}
                >
                  <Trans>session-registration.edit-candidate-with-voucher-modal.description</Trans>
                </ConfirmModal>
              )}
            </Fragment>
          )}
          {!displayOnly && !loading && !notEditable && (
            <Fragment>
              <button
                onClick={() => setDeleteModalOpen(true)}
                data-cypress="buying-process.remove-candidate"
                aria-label="open-delete-modal"
              >
                <Icon name={'trash'} aria-hidden={'true'} />
              </button>
              <ConfirmModal
                isOpen={isDeleteModalOpen}
                title={t('session-registration.remove-candidate-modal.title')}
                onClose={() => setDeleteModalOpen(false)}
                buttons={[
                  {
                    label: t('common.cancel'),
                    className: 'button--transparent-light',
                    onClick: () => setDeleteModalOpen(false),
                  },
                  {
                    label: loading ? <Loader /> : t('session-registration.remove-candidate-modal.delete'),
                    loading,
                    onClick: () => onRemoveConfirm(),
                    'data-cypress': 'buying-process.confirm-remove-candidate',
                  },
                ]}
              >
                <Trans>
                  {hasVoucher
                    ? 'session-registration.remove-candidate-with-voucher-modal.description'
                    : 'session-registration.remove-candidate-modal.description'}
                </Trans>
              </ConfirmModal>
            </Fragment>
          )}
        </div>
      </div>
    </div>
  );
};

CandidateWidget.propTypes = {
  candidate: PropTypes.shape({
    token: PropTypes.string,
    firstname: PropTypes.string.isRequired,
    lastname: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    dateOfBirth: PropTypes.string,
    phoneNumber: PropTypes.string,
  }).isRequired,
  order: PropTypes.number.isRequired,
  showForm: PropTypes.bool,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  hasVoucher: PropTypes.bool,
  notEditable: PropTypes.bool,
  loading: PropTypes.bool,
  productsAndServices: PropTypes.shape({
    correctionProductsAndServices: PropTypes.array,
    certificateProductsAndServices: PropTypes.array,
    discountProductsAndServices: PropTypes.array,
  }),
  sessionId: PropTypes.number.isRequired,
  testType: PropTypes.shape({
    translationKey: PropTypes.string,
  }),
  withPicture: PropTypes.bool,
  withDateOfBirth: PropTypes.bool,
  displayOnly: PropTypes.bool,
};

export default CandidateWidget;
